import { ImageMetadata } from '../services/api';

export interface PagePaletteColor {
    key: string;
    label: string;
    hex: string;
    count: number;
}

const normalizeColorToken = (value?: string | null): string => (value || '').trim().toLowerCase();

export const getImageColorTokens = (image: ImageMetadata): string[] => {
    const tokens = new Set<string>();

    if (Array.isArray(image.color_index)) {
        image.color_index.forEach(color => {
            [color.key, color.hex, color.source_hex].forEach(value => {
                const token = normalizeColorToken(value);
                if (token) tokens.add(token);
            });
        });
    }

    if (Array.isArray(image.color_palette)) {
        image.color_palette.forEach(color => {
            [color.hex, color.bucket, color.bucket_hex].forEach(value => {
                const token = normalizeColorToken(value);
                if (token) tokens.add(token);
            });
        });
    }

    return Array.from(tokens);
};

export const imageHasColorToken = (image: ImageMetadata, colorKey?: string | null): boolean => {
    const normalized = normalizeColorToken(colorKey);
    if (!normalized) return true;
    return getImageColorTokens(image).includes(normalized);
};

export const buildPagePalette = (images: ImageMetadata[]): PagePaletteColor[] => {
    const byKey = new Map<string, PagePaletteColor>();

    images.forEach(image => {
        const seenForImage = new Set<string>();

        if (Array.isArray(image.color_index)) {
            image.color_index.forEach(color => {
                const key = normalizeColorToken(color.key || color.hex || color.source_hex);
                if (!key || seenForImage.has(key)) return;
                seenForImage.add(key);
                const current = byKey.get(key);
                if (current) {
                    current.count += 1;
                    return;
                }
                byKey.set(key, {
                    key,
                    label: color.label || color.key || key,
                    hex: color.source_hex || color.hex || key,
                    count: 1
                });
            });
        }

        if (seenForImage.size === 0 && Array.isArray(image.color_palette)) {
            image.color_palette.forEach(color => {
                const key = normalizeColorToken(color.hex || color.bucket || color.bucket_hex);
                if (!key || seenForImage.has(key)) return;
                seenForImage.add(key);
                const current = byKey.get(key);
                if (current) {
                    current.count += 1;
                    return;
                }
                byKey.set(key, {
                    key,
                    label: color.bucket_label || color.bucket || color.hex || key,
                    hex: color.hex || color.bucket_hex || key,
                    count: 1
                });
            });
        }
    });

    return Array.from(byKey.values()).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
};
